import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactJson from 'react-json-view';

import { format } from '../../../utils/Date';
import '../ProjectLog/LogStyling.css';
import { send_request } from '../../../utils/Request';
import { isLegacyForm } from '../../../utils/formTypeConstants';
import useProjectStore from '../../ProjectCanvas/useProjectStore';
import {
  SESSION_IO_VIEW_MODES,
  hasIoEnvelope,
  getDisplayStatus,
  getDisplayError,
  getStandardInputData,
  getStandardOutputData,
  getInputBindingsData,
  getExportMapData,
  getDiagnosticsData,
  getRawIoData,
  shouldShowStandardExports,
  getStandardExportSummaryData,
} from './sessionIoViewModel';
import { isSessionIoV1RenderingEnabled } from '../../../constants/featureFlags';
import { emitSessionPaneRenderPath } from '../../../utils/rolloutTelemetry';
import PanelHeader from '../../common/PanelHeader';

// MUI
import { styled } from '@mui/styles';
import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
  Box,
  IconButton,
  TextField,
  useTheme,
} from '@mui/material';

// MUI Icons
import {
  CancelPresentation,
  Error,
  Info,
  ReplayRounded,
  ChevronLeft,
  ChevronRight,
  Assignment,
  ContentCopy,
} from '@mui/icons-material';
import config from '../../../config';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import { capitalizeLetter, getComponentDisplayName } from '../ProjectLog/LogHelpers';
import ComponentWasReRunBox from '../ProjectLog/ComponentWasReRunBox';
import { ComponentRegistry } from '../../ProjectCanvas/ComponentRegistry';
import Drawer from '@mui/material/Drawer';
import DownloadIcon from '@mui/icons-material/Download';
import './WorkflowProgressView.css';
import JsonTable from './JsonTable';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewListIcon from '@mui/icons-material/ViewList';
import JsonChips from './JsonChips';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CodeEditorModal from './CodeEditorModal';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import { processApiComponentOutput } from '../../../utils/ApiComponentOutputProcessor';

const ActionButton = styled(Button)(({ bgColor, hoverBgColor, blackText, theme }) => ({
  backgroundColor: bgColor || theme.palette.background.paper,
  color: blackText ? theme.palette.text.primary : '#FFF',
  fontSize: '13px',
  display: 'flex',
  justifyContent: 'center',
  textTransform: 'none',
  padding: '6px 12px',
  borderRadius: '4px',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: hoverBgColor || theme.palette.action.hover,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.disabled,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
}));

const AiFixButton = styled(Button)({
  backgroundColor: '#944BA8',
  color: '#FFFFFF',
  fontSize: '13px',
  borderRadius: '4px',
  textTransform: 'none',
  padding: '6px 12px',
  '&:hover': {
    backgroundColor: '#7A3D8C',
  },
});

const Cancel = styled(CancelPresentation)({
  color: '#FFF',
  height: 'auto',
  width: '16px',
  marginRight: '4px',
});

const Loader = styled(CircularProgress)({
  position: 'absolute',
  color: 'rgba(33, 150, 243, 0.55)',
  width: '16px !important',
  height: '16px !important',
});

const ActionButtonsContainer = styled(Grid)({
  display: 'flex',
  gap: '8px',
  paddingBottom: '12px',
});

const TimelineContainer = styled(Grid)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  overflow: 'hidden',
  marginTop: '12px',
}));

const TimelineHeader = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.background.subtle,
  padding: '8px 12px',
  fontWeight: 500,
  fontSize: '14px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const TimelineBody = styled(Grid)({
  padding: '12px',
});

const InfoText = styled('span')({
  display: 'flex',
  alignItems: 'center',
  fontSize: '13px',
  '& .MuiSvgIcon-root': {
    fontSize: '14px',
    marginRight: '4px',
    opacity: 0.7,
  },
});

const getJsonTheme = (isDark) => {
  const fg = isDark ? '255, 255, 255' : '1, 1, 1';
  const fg2 = isDark ? '255, 255, 255' : '0, 0, 0';
  return {
    base00: `rgba(${fg}, 0)`,
    base01: `rgba(${fg}, 0.1)`,
    base02: `rgba(${fg2}, 0.2)`,
    base03: `rgba(${fg}, 0.3)`,
    base04: `rgba(${fg2}, 0.4)`,
    base05: `rgba(${fg}, 0.5)`,
    base06: `rgba(${fg}, 0.6)`,
    base07: `rgba(${fg}, 0.7)`,
    base08: `rgba(${fg2}, 1)`,
    base09: `rgba(${fg2}, 1)`,
    base0A: `rgba(${fg2}, 1)`,
    base0B: `rgba(${fg2}, 1)`,
    base0C: `rgba(${fg2}, 1)`,
    base0D: `rgba(${fg2}, 1)`,
    base0E: `rgba(${fg}, 0.7)`,
    base0F: `rgba(${fg}, 0.7)`,
  };
};

const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: '4px',
}));

const TerminateButton = styled(ActionButton)(({ isLatestRun, theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: isLatestRun ? '#B00020' : theme.palette.text.disabled,
  border: `1px solid ${isLatestRun ? '#B00020' : theme.palette.text.disabled}`,
  '& .MuiSvgIcon-root': {
    color: isLatestRun ? '#B00020' : theme.palette.text.disabled,
  },
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    borderColor: isLatestRun ? '#B00020' : theme.palette.text.disabled,
    color: isLatestRun ? '#B00020' : theme.palette.text.disabled,
  },
  '&:disabled': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.disabled,
    borderColor: theme.palette.divider,
  },
}));
styled(Typography)({
  fontWeight: '600',
  fontSize: '14px',
  lineHeight: '16.41px',
  letterSpacing: '0',
  marginBottom: '4px',
});
styled(Typography)(({ theme }) => ({
  fontWeight: '400',
  fontSize: '13px',
  lineHeight: '16.41px',
  letterSpacing: '0',
  color: theme.palette.text.secondary,
  marginBottom: '8px',
}));
const COMPONENT_ERROR = {
  INTERNAL_ERROR: "Cripes! Something's gone wrong. If the problem persists please quote",
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButton-root': {
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    padding: '4px 8px',
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.primary,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '16px',
    },
  },
}));

// Replace with modern thread selector components that work well with many threads
const ThreadSelectorContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
  gap: '8px',
});

const ThreadLabel = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    fontSize: '16px',
    marginRight: '4px',
  },
}));

const ThreadCounter = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

const NavigationButton = styled(IconButton)({
  padding: '2px',
  '&.Mui-disabled': {
    opacity: 0.3,
  },
});

const ThreadSelectInput = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: '4px',
    fontSize: '13px',
    height: '32px',
    width: '110px',
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 8px',
  },
  '& .MuiSelect-select': {
    minHeight: 'unset',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
}));

function SessionInfoModal({
  modalData,
  setModalData,
  classes,
  sessionId,
  getWorkflowProgressData,
  isTest,
  projectTestId,
  sessionData,
  setSessionData,
  handleAlterThreadPath,
  isLatestRun,
  reRuns,
  setIsLatestRun,
  setLoadReRun,
  loadReRun,
  isShowRerunMenu,
  setShowRerunMenu,
  projectId,
  nodes,
  reactFlowInstance,
  isSplitView,
  triggerRefresh,
  refreshSessions,
}) {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const jsonTheme = getJsonTheme(isDark);
  const aceTheme = isDark ? 'monokai' : 'github';
  const [terminateLoading, setTerminateLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewModes, setViewModes] = useState({
    inputs: 'tree',
    outputs: 'tree',
    data: 'tree',
    formConfig: 'tree',
  });
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [copiedError, setCopiedError] = useState(false);
  const userName = useProjectStore.getState().userName || 'anonymous';
  const clientId = useProjectStore(s => s.clientId);
  const ioViewStorageKey = `wf86.sessionInfo.ioViewMode.${userName}`;
  const [ioViewMode, setIoViewMode] = useState(() => {
    try {
      const stored = window.localStorage.getItem(ioViewStorageKey);
      return stored === SESSION_IO_VIEW_MODES.ADVANCED
        ? SESSION_IO_VIEW_MODES.ADVANCED
        : SESSION_IO_VIEW_MODES.STANDARD;
    } catch (_) {
      return SESSION_IO_VIEW_MODES.STANDARD;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(ioViewStorageKey, ioViewMode);
    } catch (_) {}
  }, [ioViewMode, ioViewStorageKey]);

  const [resolvedAiAgentSessions, setResolvedAiAgentSessions] = useState({});
  const aiAgentLookupStateRef = useRef({});

  const copyErrorToClipboard = errorText => {
    navigator.clipboard.writeText(errorText).then(() => {
      setCopiedError(true);
      setTimeout(() => setCopiedError(false), 2000);
    });
  };

  const getAiAgentSessionKey = component => {
    const threadId = component?.thread?.threadId || 'main';
    return `${component?.componentId || 'unknown'}:${component?.startAt || 'nostart'}:${threadId}`;
  };

  const getAiAgentId = component =>
    component?.data?.agentId ||
    component?.data?.data?.agentId ||
    component?.data?.data?.aiAgentData?.agentId ||
    component?.data?.aiAgentData?.agentId ||
    null;

  const getAiChatSessionId = component =>
    component?.data?.chatSessionId || component?.data?.data?.chatSessionId || null;

  const toTimestamp = value => {
    const ts = new Date(value).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  };

  const resolveAiAgentSessionLink = async component => {
    const workflowSessionId = sessionData?.projectSessionId;
    const agentId = getAiAgentId(component);
    if (!workflowSessionId || !agentId) return;

    const componentKey = getAiAgentSessionKey(component);
    const now = Date.now();
    const lastLookupAt = aiAgentLookupStateRef.current[componentKey] || 0;
    if (now - lastLookupAt < 2000) return;
    aiAgentLookupStateRef.current[componentKey] = now;

    try {
      const response = await send_request(`project-service/agent/${agentId}/sessions`, '', {}, 'GET');
      const allSessions = Array.isArray(response?.data) ? response.data : [];
      const workflowSessions = allSessions.filter(
        session => session?.workflowSessionId === workflowSessionId && session?.chatSessionId
      );
      if (!workflowSessions.length) return;

      const componentStartAt = toTimestamp(component?.startAt);
      const selectedSession = [...workflowSessions].sort((a, b) => {
        if (componentStartAt) {
          const aDiff = Math.abs(toTimestamp(a?.createdAt) - componentStartAt);
          const bDiff = Math.abs(toTimestamp(b?.createdAt) - componentStartAt);
          if (aDiff !== bDiff) return aDiff - bDiff;
        }
        return toTimestamp(b?.updatedAt || b?.createdAt) - toTimestamp(a?.updatedAt || a?.createdAt);
      })[0];

      if (!selectedSession?.chatSessionId) return;

      setResolvedAiAgentSessions(prev => ({
        ...prev,
        [componentKey]: {
          agentId: selectedSession?.agentId || agentId,
          chatSessionId: selectedSession.chatSessionId,
        },
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const isOpen = modalData.componentId != null;

  useEffect(() => {
    if (!isOpen || modalData?.type !== 'ai_agent') return;
    if (getAiChatSessionId(modalData)) return;
    resolveAiAgentSessionLink(modalData);
  }, [isOpen, modalData, sessionData?.projectSessionId]);

  const handleViewModeChange = (section, newValue) => {
    if (newValue !== null) {
      setViewModes(prev => ({
        ...prev,
        [section]: newValue,
      }));
    }
  };
  const renderStatus = (status, asChip = false) => {
    let color;
    let bgColor;
    let borderColor;

    switch (status) {
      case 'FAIL':
        color = '#b00020';
        bgColor = 'rgba(176, 0, 32, 0.1)';
        borderColor = 'rgba(176, 0, 32, 0.2)';
        break;
      case 'PAUSED':
        color = '#ed6c02';
        bgColor = 'rgba(237, 108, 2, 0.1)';
        borderColor = 'rgba(237, 108, 2, 0.2)';
        break;
      case 'SUCCESS':
      case 'SUCCESS_END':
        color = '#31a633';
        bgColor = 'rgba(49, 166, 51, 0.1)';
        borderColor = 'rgba(49, 166, 51, 0.2)';
        break;
      case 'INPROGRESS':
        color = '#ff9d0b';
        bgColor = 'rgba(255, 157, 11, 0.1)';
        borderColor = 'rgba(255, 157, 11, 0.2)';
        break;
      case 'WAITING':
        color = '#5d6bc4';
        bgColor = 'rgba(93, 107, 196, 0.1)';
        borderColor = 'rgba(93, 107, 196, 0.2)';
        break;
      case 'TERMINATED':
        color = '#b00020';
        bgColor = 'rgba(176, 0, 32, 0.1)';
        borderColor = 'rgba(176, 0, 32, 0.2)';
        break;
      default:
        color = '#666666';
        bgColor = 'rgba(102, 102, 102, 0.1)';
        borderColor = 'rgba(102, 102, 102, 0.2)';
    }

    let displayStatus = status;
    if (status === 'FAIL') {
      displayStatus = 'Error';
    }
    if (status === 'INPROGRESS') {
      displayStatus = 'In Progress';
    }

    if (!isLatestRun) {
      color = 'rgba(156, 163, 175, 1)';
      bgColor = 'rgba(156, 163, 175, 0.1)';
      borderColor = 'rgba(156, 163, 175, 0.2)';
    }

    if (asChip) {
      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            borderRadius: '6px',
            backgroundColor: bgColor,
            border: `1px solid ${borderColor}`,
            fontSize: '12px',
            fontWeight: 500,
            color: color,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {status === 'INPROGRESS' && (
            <CircularProgress size={12} thickness={4} sx={{ color: color }} />
          )}
          {capitalizeLetter(displayStatus.toLowerCase())}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: color,
          fontWeight: 'bold',
          fontSize: '13px',
        }}
      >
        {status === 'INPROGRESS' && <CircularProgress size={16} style={{ marginRight: '8px' }} />}
        {capitalizeLetter(displayStatus.toLowerCase())}
      </Box>
    );
  };

  const terminateAssignTask = (projectSessionId, component) => {
    let componentId = component.componentId;
    let thread = component.thread;

    if (!projectSessionId) return;
    setTerminateLoading(true);

    let reqBody = {
      isTest: isTest,
    };

    if (thread != null) reqBody['threadid'] = thread.threadId;

    let url = `project-service/project/terminate_task/${projectSessionId}/${componentId}`;

    send_request(url, '', reqBody, 'post')
      .then(res => {
        getWorkflowProgressData();
      })
      .then(() => {
        setTerminateLoading(false);
        setModalData({});
      })
      .catch(err => {
        console.log(err);
        setTerminateLoading(false);
      });
  };

  const retryComponentSession = (projectSessionId, component) => {
    let componentId = component.componentId;
    let thread = component.thread;

    let url =
      'project-service/project-session/retry_component_session/' +
      projectSessionId +
      '/' +
      componentId;

    if (thread && thread.threadId && thread.threadId != '') {
      //thread involved
      url = url + '/' + thread.threadId;
    }

    setTerminateLoading(true);

    send_request(url, '', null, 'post')
      .then(res => {
        getWorkflowProgressData();
        // Trigger refresh polling after successful retry
        if (triggerRefresh) {
          triggerRefresh();
        }
      })
      .then(() => {
        setTerminateLoading(false);
        setModalData({});
      })
      .catch(err => {
        console.log(err);
        setTerminateLoading(false);
      });
  };

  const openSessionPageBySessionId = selectedSessionId => {
    const url = `/project/logs/progress_view/${projectId}/${selectedSessionId}?test=${isTest}`;
    window.open(url, '_blank');
  };

  const rerunComponentSession = (projectSessionId, component) => {
    const componentId = component?.componentId;
    const version = sessionData.projectVersion;
    const componentType = component?.type;

    let body = {
      inputtedPlaceholders: {},
      projectId: projectId,
      componentId: componentId,
      componentType: componentType,
      versionToRun: version,
      sessionIdsToExecuteWorkflow: [projectSessionId],
      isTest: isTest,
    };

    const url = `project-service/project-session/force_start_workflow`;
    send_request(url, body, null, 'POST')
      .then(response => {
        getWorkflowProgressData();
        const newSessionId = response?.data?.[sessionId];

        //For session page (Not the session modal)
        if (window.location.href.includes('/project/logs/progress_view') && newSessionId) {
          openSessionPageBySessionId(newSessionId);
        } else {
          refreshSessions(isTest, true);
        }
      })
      .catch(err => {
        console.log(err);
        setTerminateLoading(false);
      })
      .finally(() => {
        setShowRerunMenu(false);
      });
  };

  function isInputString(jsonInput) {
    if (typeof jsonInput === 'string') {
      return false;
    }
    return true;
  }

  function codeLangToAceEditorType(codeLang) {
    if (codeLang == 'NodeJS_16' || codeLang == 'javascript') {
      return 'javascript';
    }
    return 'python';
  }

  const fetchPlaceholderAndValues = async () => {
    const url = `project-service/project-session/get_existing_session_placeholder_data/${sessionData?.projectSessionId}`;
    const placeholders = await fetchAllPlaceholdersFromSession();
    let json = await send_request(url, placeholders, '', 'POST');
    return json?.data;
  };

  const fetchAllPlaceholdersFromSession = async () => {
    let url = `component-service/placeholder/${projectId}/${modalData.componentId}`;
    const version = sessionData.projectVersion;
    let reqData = null;
    if (version) reqData = { workflowVersion: version };

    let json = await send_request(url, null, reqData, 'get');

    if (!json?.data?.placeholders) return;
    return json.data.placeholders;
  };

  const renderJsonData = (data, name, section, component = null) => {
    const viewMode = viewModes[section] || 'tree';

    // Process API component data to remove legacy data and format better
    const processedData = processApiComponentOutput(data, component, section);
    const nullCheckedData = processedData || {};

    return (
      <Grid sx={{ width: '100%', overflowX: 'hidden' }}>
        {viewMode === 'table' ? (
          <JsonTable data={nullCheckedData} />
        ) : viewMode === 'keyValue' ? (
          <JsonChips data={nullCheckedData} />
        ) : (
          <ReactJson
            src={nullCheckedData}
            name={false}
            iconStyle="square"
            displayDataTypes={false}
            className="reactJson"
            theme={jsonTheme}
            style={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
            collapsed={2}
            collapseStringsAfterLength={80}
            displayObjectSize={false}
            enableClipboard={copy => {
              navigator.clipboard.writeText(JSON.stringify(copy.src, null, 2));
            }}
            displayArrayKey={false}
            shouldCollapse={field => {
              if (Array.isArray(field.value) && field.value.length > 100) {
                return true;
              }
              return false;
            }}
          />
        )}
      </Grid>
    );
  };

  const isInternalError = component => {
    return component.error.includes(COMPONENT_ERROR.INTERNAL_ERROR);
  };

  const getTimelineContent = (component, classes) => {
    let formSessionId = '';
    if (component.data) {
      formSessionId =
        component.data.formSessionId ||
        (component.data.data && component.data.data.formSessionId) ||
        '';
    }
    let formSessionURL =
      component.data
        ? component.data.formSessionURL || (component.data.data && component.data.data.formSessionURL)
        : '';
    const showLegacyFormSessionLink =
      isLegacyForm(component.type) &&
      component.data &&
      (formSessionId || formSessionURL || component.data.data);
    const aiAgentSessionKey = getAiAgentSessionKey(component);
    const resolvedAiSession = resolvedAiAgentSessions[aiAgentSessionKey];
    const aiAgentId = getAiAgentId(component) || resolvedAiSession?.agentId;
    const chatSessionId = getAiChatSessionId(component) || resolvedAiSession?.chatSessionId;
    const aiAgentSessionUrl =
      aiAgentId && chatSessionId
        ? `/agents/${aiAgentId}?tab=sessions&sessionId=${chatSessionId}`
        : null;

    const noInputData = componentData => {
      const noInput = { ...componentData };
      delete noInput['.inputData'];
      return noInput;
    };

    const displayStatus = getDisplayStatus(component);
    const displayError = getDisplayError(component);

    // Emit render path telemetry (once per render, fire-and-forget)
    const useIoPath = hasIoEnvelope(component) && isSessionIoV1RenderingEnabled(clientId);
    try {
      emitSessionPaneRenderPath({
        projectId: sessionData?.projectId,
        clientId,
        path: useIoPath ? 'io' : 'legacy',
      });
    } catch (_) {}

    const renderIoPanels = () => {
      const standardInput = getStandardInputData(component);
      const standardOutput = getStandardOutputData(component);

      return (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Grid container alignItems="center" justifyContent="space-between">
                <InfoText>Session View:</InfoText>
                <StyledToggleButtonGroup
                  value={ioViewMode}
                  exclusive
                  onChange={(event, next) => next && setIoViewMode(next)}
                  size="small"
                >
                  <ToggleButton value={SESSION_IO_VIEW_MODES.STANDARD}>Standard</ToggleButton>
                  <ToggleButton value={SESSION_IO_VIEW_MODES.ADVANCED}>Advanced</ToggleButton>
                </StyledToggleButtonGroup>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <TimelineContainer>
                <TimelineHeader item xs={12}>Inputs</TimelineHeader>
                <TimelineBody>{renderJsonData(standardInput, 'Input Data', 'inputs', component)}</TimelineBody>
              </TimelineContainer>
            </Grid>

            <Grid item xs={12}>
              <TimelineContainer>
                <TimelineHeader item xs={12}>Outputs</TimelineHeader>
                <TimelineBody>{renderJsonData(standardOutput, 'Output Data', 'outputs', component)}</TimelineBody>
              </TimelineContainer>
            </Grid>

            {ioViewMode === SESSION_IO_VIEW_MODES.STANDARD && shouldShowStandardExports(component) && (
              <Grid item xs={12}>
                <TimelineContainer>
                  <TimelineHeader item xs={12}>Available for Next Steps</TimelineHeader>
                  <TimelineBody>
                    {renderJsonData(getStandardExportSummaryData(component), 'Export Summary', 'data', component)}
                  </TimelineBody>
                </TimelineContainer>
              </Grid>
            )}

            {ioViewMode === SESSION_IO_VIEW_MODES.ADVANCED && (
              <>
                <Grid item xs={12}>
                  <TimelineContainer>
                    <TimelineHeader item xs={12}>Input Bindings</TimelineHeader>
                    <TimelineBody>
                      {renderJsonData(getInputBindingsData(component), 'Input Bindings', 'data', component)}
                    </TimelineBody>
                  </TimelineContainer>
                </Grid>
                <Grid item xs={12}>
                  <TimelineContainer>
                    <TimelineHeader item xs={12}>Export Map</TimelineHeader>
                    <TimelineBody>
                      {renderJsonData(getExportMapData(component), 'Export Map', 'data', component)}
                    </TimelineBody>
                  </TimelineContainer>
                </Grid>
                <Grid item xs={12}>
                  <TimelineContainer>
                    <TimelineHeader item xs={12}>Diagnostics</TimelineHeader>
                    <TimelineBody>
                      {renderJsonData(getDiagnosticsData(component), 'Diagnostics', 'data', component)}
                    </TimelineBody>
                  </TimelineContainer>
                </Grid>
                <Grid item xs={12}>
                  <TimelineContainer>
                    <TimelineHeader item xs={12}>Raw io.v1 JSON</TimelineHeader>
                    <TimelineBody>{renderJsonData(getRawIoData(component), 'Raw io', 'data', component)}</TimelineBody>
                  </TimelineContainer>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      );
    };

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TimelineContainer>
            <TimelineHeader item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                {getComponentDisplayName(component.type)}
              </Box>
            </TimelineHeader>
            <TimelineBody container spacing={2}>
              {showLegacyFormSessionLink ? (
                <Grid item xs={12}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={3}>
                      <InfoText>Form:</InfoText>
                    </Grid>
                    <Grid item xs={9}>
                      {component.status === 'INPROGRESS' ? (
                        <StyledSkeleton variant="rectangular" height="30px" width="90%" />
                      ) : formSessionURL && !formSessionId ? (
                        <a className="link" href={formSessionURL} target="_blank" rel="noopener noreferrer">
                          {formSessionURL}
                        </a>
                      ) : !isTest ? (
                        <a
                          className="link"
                          href={`${config.FORM.FORM_FRONT_END.replace(/\/+$/, '')}/form/${
                            component.componentId
                          }/${formSessionId}`.replace(/([^:]\/)\/+/g, '$1')}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`${config.FORM.FORM_FRONT_END.replace(/\/+$/, '')}/form/${
                            component.componentId
                          }/${formSessionId}`.replace(/([^:]\/)\/+/g, '$1')}
                        </a>
                      ) : (
                        <a
                          className="link"
                          href={`${config.FORM.FORM_FRONT_END.replace(/\/+$/, '')}/form/form_test/${
                            component.componentId
                          }/${
                            component.type !== 'form' ? `${projectTestId}/` : ''
                          }${formSessionId}`.replace(/([^:]\/)\/+/g, '$1')}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`${config.FORM.FORM_FRONT_END.replace(/\/+$/, '')}/form/form_test/${
                            component.componentId
                          }/${
                            component.type !== 'form' ? `${projectTestId}/` : ''
                          }${formSessionId}`.replace(/([^:]\/)\/+/g, '$1')}
                        </a>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                ''
              )}
              {component.status === 'SUCCESS' &&
              component.type === 'document_editor' &&
              component.data ? (() => {
                const payload = component.data.data ?? component.data;
                const docKey = Object.keys(payload).find(k => k !== '.inputData');
                const docUrl = docKey ? payload[docKey] : null;
                return docUrl ? (
                  <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={3}>
                        <InfoText>Document:</InfoText>
                      </Grid>
                      <Grid item xs={9}>
                        <a
                          className="link"
                          href={docUrl}
                          target="_blank"
                        >
                          {docUrl}
                        </a>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : '';
              })() : (
                ''
              )}
              {component.type === 'ai_agent' ? (
                <Grid item xs={12}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={3}>
                      <InfoText>Chat Session:</InfoText>
                    </Grid>
                    <Grid item xs={9}>
                      {aiAgentSessionUrl ? (
                        <a className="link" href={aiAgentSessionUrl} target="_blank">
                          View Chat Session
                        </a>
                      ) : component.status === 'INPROGRESS' ? (
                        <StyledSkeleton variant="rectangular" height="30px" width="90%" />
                      ) : (
                        '-'
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                ''
              )}
              <Grid item xs={12}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={3}>
                    <InfoText>Started:</InfoText>
                  </Grid>
                  <Grid item xs={9}>
                    {format(component.startAt, useProjectStore.getState().timezone)}
                  </Grid>
                </Grid>
              </Grid>
              {component.status !== 'WAITING' &&
                component.status !== 'PAUSED' &&
                component.status !== 'INPROGRESS' && (
                  <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={3}>
                        <InfoText>Ended:</InfoText>
                      </Grid>
                      <Grid item xs={9}>
                        {format(component.endAt, useProjectStore.getState().timezone)}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              <Grid item xs={12}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={3}>
                    <InfoText>Status:</InfoText>
                  </Grid>
                  <Grid item xs={9}>
                    {renderStatus(displayStatus)}
                  </Grid>
                </Grid>
              </Grid>
              {component.retryCount != null && component.retryCount > 0 && (
                <Grid item xs={12}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={3}>
                      <InfoText>Auto-retry attempts:</InfoText>
                    </Grid>
                    <Grid item xs={9}>{component.retryCount}</Grid>
                  </Grid>
                </Grid>
              )}
              {component.type === 'code' &&
                (component?.data?.data?.['.inputData']?.code ||
                  component?.data?.['.inputData']?.code ||
                  component?.data?.data?.code ||
                  component?.data?.code) && (
                  <Grid item xs={12}>
                    <Grid container marginBottom={2}>
                      <Grid
                        item
                        xs={12}
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ marginBottom: '8px' }}
                      >
                        <InfoText>Code:</InfoText>
                        <Button
                          startIcon={<OpenInFullIcon />}
                          size="small"
                          onClick={() => setIsCodeModalOpen(true)}
                          sx={{ textTransform: 'none', fontSize: '13px' }}
                        >
                          Open
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <AceEditor
                          mode={codeLangToAceEditorType(
                            component?.data?.data?.['.inputData']?.language ||
                              component?.data?.['.inputData']?.language ||
                              component?.data?.data?.language ||
                              component?.data?.language ||
                              component?.data?.data?.['.inputData']?.codeLang ||
                              component?.data?.['.inputData']?.codeLang ||
                              component?.data?.data?.codeLang ||
                              component?.data?.codeLang
                          )}
                          theme={aceTheme}
                          name="code-editor"
                          editorProps={{ $blockScrolling: true }}
                          value={
                            component?.data?.data?.['.inputData']?.code ||
                            component?.data?.['.inputData']?.code ||
                            component?.data?.data?.code ||
                            component?.data?.code
                          }
                          readOnly={true}
                          width="100%"
                          minLines={2}
                          maxLines={20}
                          wrapEnabled={true}
                        />
                      </Grid>
                    </Grid>

                    <CodeEditorModal
                      open={isCodeModalOpen}
                      onClose={() => setIsCodeModalOpen(false)}
                      code={
                        component?.data?.data?.['.inputData']?.code ||
                        component?.data?.['.inputData']?.code ||
                        component?.data?.data?.code ||
                        component?.data?.code
                      }
                      codeLang={
                        component?.data?.data?.['.inputData']?.language ||
                        component?.data?.['.inputData']?.language ||
                        component?.data?.data?.language ||
                        component?.data?.language ||
                        component?.data?.data?.['.inputData']?.codeLang ||
                        component?.data?.['.inputData']?.codeLang ||
                        component?.data?.data?.codeLang ||
                        component?.data?.codeLang
                      }
                    />
                  </Grid>
                )}
              {displayError && (
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Grid container alignItems="flex-start" spacing={1}>
                        <Grid item xs={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <InfoText>Error:</InfoText>
                          </Box>
                        </Grid>
                        <Grid item xs={9}>
                          <Box sx={{ position: 'relative' }}>
                            <Typography
                              className={'warning preformatted'}
                              sx={{
                                fontSize: '13px',
                                padding: '8px',
                                paddingRight: '40px',
                                backgroundColor: 'rgba(176, 0, 32, 0.05)',
                                borderRadius: '4px',
                                border: '1px solid rgba(176, 0, 32, 0.1)',
                              }}
                            >
                              {displayError}
                            </Typography>
                            <Tooltip title={copiedError ? 'Copied!' : 'Copy error'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => copyErrorToClipboard(displayError)}
                                  sx={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    padding: '4px',
                                    backgroundColor: copiedError
                                      ? 'rgba(76, 175, 80, 0.1)'
                                      : 'background.paper',
                                    '&:hover': {
                                      backgroundColor: copiedError
                                        ? 'rgba(76, 175, 80, 0.2)'
                                        : 'action.hover',
                                    },
                                  }}
                                >
                                  <ContentCopy
                                    sx={{
                                      fontSize: '16px',
                                      color: copiedError ? '#4CAF50' : 'text.secondary',
                                    }}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {hasIoEnvelope(component) &&
                ioViewMode === SESSION_IO_VIEW_MODES.STANDARD &&
                displayError && (
                  <Grid item xs={12}>
                    <Button
                      size="small"
                      onClick={() => setIoViewMode(SESSION_IO_VIEW_MODES.ADVANCED)}
                      sx={{ textTransform: 'none' }}
                    >
                      View advanced details
                    </Button>
                  </Grid>
                )}
            </TimelineBody>
          </TimelineContainer>
        </Grid>

        {hasIoEnvelope(component) && isSessionIoV1RenderingEnabled(clientId) ? (
          renderIoPanels()
        ) : (
        <Grid item xs={12} sx={{ mt: 1 }}>
          {/*Check if the component is a form*/}
          {isLegacyForm(component.type) ? (
            /*Check if the form component is successful*/
            component.status === 'SUCCESS' ? (
              <Grid item xs={12}>
                <TimelineContainer>
                  <TimelineHeader item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      Data
                      <StyledToggleButtonGroup
                        value={viewModes.data}
                        exclusive
                        onChange={(event, newValue) => handleViewModeChange('data', newValue)}
                        aria-label="data-view-mode"
                        size="small"
                      >
                        <ToggleButton value="tree" aria-label="tree view">
                          <AccountTreeIcon />
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="table view">
                          <TableChartIcon />
                        </ToggleButton>
                        <ToggleButton value="keyValue" aria-label="key-value view">
                          <ViewListIcon />
                        </ToggleButton>
                      </StyledToggleButtonGroup>
                    </Box>
                  </TimelineHeader>
                  <TimelineBody>
                    {renderJsonData(
                      component.data && component.data.data
                        ? noInputData(component.data.data)
                        : component.data
                        ? noInputData(component.data)
                        : {},
                      'Data',
                      'data',
                      component
                    )}
                  </TimelineBody>
                </TimelineContainer>
              </Grid>
            ) : (
              /* When it is not a successful form component */
              <Grid item xs={12}>
                <TimelineContainer>
                  <TimelineHeader item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      Form Configuration
                      <StyledToggleButtonGroup
                        value={viewModes.formConfig}
                        exclusive
                        onChange={(event, newValue) => handleViewModeChange('formConfig', newValue)}
                        aria-label="form-config-view-mode"
                        size="small"
                      >
                        <ToggleButton value="tree" aria-label="tree view">
                          <AccountTreeIcon />
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="table view">
                          <TableChartIcon />
                        </ToggleButton>
                        <ToggleButton value="keyValue" aria-label="key-value view">
                          <ViewListIcon />
                        </ToggleButton>
                      </StyledToggleButtonGroup>
                    </Box>
                  </TimelineHeader>
                  <TimelineBody>
                    {renderJsonData(
                      component.data && component.data.data
                        ? noInputData(component.data.data)
                        : component.data
                        ? noInputData(component.data)
                        : {},
                      'Form Configuration',
                      'formConfig',
                      component
                    )}
                  </TimelineBody>
                </TimelineContainer>
              </Grid>
            )
          ) : /* Check if the component is Success or Success_End. Success_End is for do not continue loop condition edge case */
          component.status === 'SUCCESS' || component.status === 'SUCCESS_END' ? (
            <Grid item xs={12} container spacing={0}>
              <Grid item xs={12}>
                <TimelineContainer>
                  <TimelineHeader item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      Inputs
                      <StyledToggleButtonGroup
                        value={viewModes.inputs}
                        exclusive
                        onChange={(event, newValue) => handleViewModeChange('inputs', newValue)}
                        aria-label="input-view-mode"
                        size="small"
                      >
                        <ToggleButton value="tree" aria-label="tree view">
                          <AccountTreeIcon />
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="table view">
                          <TableChartIcon />
                        </ToggleButton>
                        <ToggleButton value="keyValue" aria-label="key-value view">
                          <ViewListIcon />
                        </ToggleButton>
                      </StyledToggleButtonGroup>
                    </Box>
                  </TimelineHeader>
                  <TimelineBody>
                    {renderJsonData(
                      component.data && component.data.data
                        ? isInputString(component.data.data['.inputData'])
                          ? component.data.data['.inputData']
                          : { Input: component.data.data['.inputData'] }
                        : component.data
                        ? isInputString(component.data['.inputData'])
                          ? component.data['.inputData']
                          : { Input: component.data['.inputData'] }
                        : {},
                      'Input Data',
                      'inputs',
                      component
                    )}
                  </TimelineBody>
                </TimelineContainer>
              </Grid>
              <Grid item xs={12} sx={{ mt: 1 }}>
                <TimelineContainer>
                  <TimelineHeader item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      Outputs
                      <StyledToggleButtonGroup
                        value={viewModes.outputs}
                        exclusive
                        onChange={(event, newValue) => handleViewModeChange('outputs', newValue)}
                        aria-label="output-view-mode"
                        size="small"
                      >
                        <ToggleButton value="tree" aria-label="tree view">
                          <AccountTreeIcon />
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="table view">
                          <TableChartIcon />
                        </ToggleButton>
                        <ToggleButton value="keyValue" aria-label="key-value view">
                          <ViewListIcon />
                        </ToggleButton>
                      </StyledToggleButtonGroup>
                    </Box>
                  </TimelineHeader>
                  <TimelineBody>
                    {renderJsonData(
                      component.data && component.data.data
                        ? noInputData(component.data.data)
                        : component.data
                        ? noInputData(component.data)
                        : {},
                      'Output Data',
                      'outputs',
                      component
                    )}
                  </TimelineBody>
                </TimelineContainer>
              </Grid>
            </Grid>
          ) : (
            /* This is for every other component status for components that are not forms */
            <Grid item xs={12} container spacing={0}>
              <Grid item xs={12}>
                <TimelineContainer>
                  <TimelineHeader item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      Inputs
                      <StyledToggleButtonGroup
                        value={viewModes.inputs}
                        exclusive
                        onChange={(event, newValue) => handleViewModeChange('inputs', newValue)}
                        aria-label="other-input-view-mode"
                        size="small"
                      >
                        <ToggleButton value="tree" aria-label="tree view">
                          <AccountTreeIcon />
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="table view">
                          <TableChartIcon />
                        </ToggleButton>
                        <ToggleButton value="keyValue" aria-label="key-value view">
                          <ViewListIcon />
                        </ToggleButton>
                      </StyledToggleButtonGroup>
                    </Box>
                  </TimelineHeader>
                  <TimelineBody>
                    {renderJsonData(
                      component?.data?.data || component?.data || {},
                      'Input Data',
                      'inputs',
                      component
                    )}
                  </TimelineBody>
                </TimelineContainer>
              </Grid>
              <Grid item xs={12} sx={{ mt: 1 }}>
                <TimelineContainer>
                  <TimelineHeader item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      Outputs
                      <StyledToggleButtonGroup
                        value={viewModes.outputs}
                        exclusive
                        onChange={(event, newValue) => handleViewModeChange('outputs', newValue)}
                        aria-label="other-output-view-mode"
                        size="small"
                      >
                        <ToggleButton value="tree" aria-label="tree view">
                          <AccountTreeIcon />
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="table view">
                          <TableChartIcon />
                        </ToggleButton>
                        <ToggleButton value="keyValue" aria-label="key-value view">
                          <ViewListIcon />
                        </ToggleButton>
                      </StyledToggleButtonGroup>
                    </Box>
                  </TimelineHeader>
                  <TimelineBody>
                    <Grid
                      container
                      alignItems="center"
                      sx={{ padding: '8px', color: 'text.disabled', fontSize: '14px' }}
                    >
                      No outputs to display
                    </Grid>
                  </TimelineBody>
                </TimelineContainer>
              </Grid>
            </Grid>
          )}
        </Grid>
        )}
        {component.wasReRun && (
          <Grid item xs={12} sx={{ mt: 1 }}>
            <ComponentWasReRunBox
              session={sessionData}
              reRuns={reRuns}
              setSession={setSessionData}
              setIsLatestRun={setIsLatestRun}
              setLoadReRun={setLoadReRun}
              calculateTimeElapsed={item => {
                return item;
              }}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  const getBase = (component, classes) => {
    let showRerunButton = true;

    let a = ComponentRegistry.filter(comp => comp.name == component.type);

    if (a[0].isTrigger || component.type == 'loop_through_list') {
      showRerunButton = false;
    }

    return (
      <>
        <ActionButtonsContainer container>
          {showRerunButton &&
            component.status != 'FAIL' &&
            component.status != 'INPROGRESS' && (
              <Tooltip title="Re-run this component">
                <span>
                  <ActionButton
                    blackText
                    startIcon={<ReplayRounded />}
                    onClick={() => rerunComponentSession(sessionId, component)}
                    disabled={terminateLoading || !isLatestRun}
                    size="small"
                  >
                    Re-run
                    {terminateLoading && <Loader size={16} />}
                  </ActionButton>
                </span>
              </Tooltip>
            )}

          {(component.status === 'WAITING' || component.status === 'INPROGRESS') && (
            <Tooltip title="Terminate this component">
              <span>
                <TerminateButton
                  onClick={() => terminateAssignTask(sessionId, component)}
                  disabled={terminateLoading || !isLatestRun}
                  startIcon={<Cancel />}
                  isLatestRun={isLatestRun}
                  size="small"
                >
                  Terminate
                  {terminateLoading && <Loader size={16} />}
                </TerminateButton>
              </span>
            </Tooltip>
          )}

          {component.status === 'FAIL' && (
            <Tooltip title="Retry this component">
              <span>
                <ActionButton
                  blackText
                  startIcon={<ReplayRounded />}
                  onClick={() => retryComponentSession(sessionId, component)}
                  disabled={terminateLoading || !isLatestRun}
                  size="small"
                >
                  Retry
                  {terminateLoading && <Loader size={16} />}
                </ActionButton>
              </span>
            </Tooltip>
          )}

          {(!a[0].isTrigger || a[0].name === 'form') &&
            modalData.componentId &&
            sessionId &&
            projectId && (
              <Tooltip title={isTest
                ? "Migrate data from the current session and re-run on the latest draft version"
                : "Migrate data from the current session and re-run on the current production version"}>
                <span>
                  <ActionButton
                    blackText
                    disabled={terminateLoading || !isLatestRun}
                    onClick={() => {
                      const basePath = isTest
                        ? `/project/view_canvas/${projectId}`
                        : `/project/production/${projectId}`;
                      window.open(
                        `${basePath}?force_start=${modalData.componentId}&selectedSession=${sessionId}`
                      );
                    }}
                    startIcon={<MoveDownIcon />}
                    size="small"
                  >
                    Re-run on latest version
                    {terminateLoading && <Loader size={16} />}
                  </ActionButton>
                </span>
              </Tooltip>
            )}

          <Tooltip title="Download component data as JSON">
            <span>
              <ActionButton
                blackText
                startIcon={<DownloadIcon />}
                onClick={() => downloadComponentData(component)}
                size="small"
              >
                Download JSON
              </ActionButton>
            </span>
          </Tooltip>
        </ActionButtonsContainer>

        {getThreadSelect(component)}

        {getTimelineContent(component, classes)}
      </>
    );
  };

  const getThreadSelect = data => {
    if (!data || data.type !== 'loop_through_list') return;
    if (!sessionData || !sessionData.components || !Array.isArray(sessionData.components))
      return null;

    let parentSEThreadId = data.thread.parentThreadId;
    let successItems = [];

    for (let sDComp of sessionData.components) {
      if (sDComp.type !== 'loop_through_list' || sDComp.status !== 'SUCCESS') continue;
      if (sDComp.thread.parentThreadId !== parentSEThreadId) continue;
      successItems.push(sDComp);
    }

    if (successItems.length <= 1) return null;

    // Find current thread index
    const currentThreadIndex = successItems.findIndex(
      item => item.thread.threadId === data.thread.threadId
    );

    // Find previous and next thread IDs for navigation
    const hasPrevious = currentThreadIndex > 0;
    const hasNext = currentThreadIndex < successItems.length - 1;
    const previousThreadId = hasPrevious
      ? successItems[currentThreadIndex - 1].thread.threadId
      : null;
    const nextThreadId = hasNext ? successItems[currentThreadIndex + 1].thread.threadId : null;

    return (
      <ThreadSelectorContainer>
        <ThreadLabel>
          <AutoAwesomeMotionIcon fontSize="small" /> Threads:
        </ThreadLabel>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <NavigationButton
            size="small"
            disabled={!hasPrevious}
            onClick={() =>
              hasPrevious && handleAlterThreadPath(data.thread.threadId, previousThreadId)
            }
          >
            <ChevronLeft fontSize="small" />
          </NavigationButton>

          <ThreadSelectInput
            select
            variant="outlined"
            size="small"
            value={data.thread.threadId}
            onChange={e => handleAlterThreadPath(data.thread.threadId, e.target.value)}
          >
            {successItems.map((item, idx) => (
              <MenuItem key={item.thread.threadId} value={item.thread.threadId}>
                Thread {idx + 1}
              </MenuItem>
            ))}
          </ThreadSelectInput>

          <NavigationButton
            size="small"
            disabled={!hasNext}
            onClick={() => hasNext && handleAlterThreadPath(data.thread.threadId, nextThreadId)}
          >
            <ChevronRight fontSize="small" />
          </NavigationButton>
        </Box>

        <ThreadCounter>of {successItems.length} threads</ThreadCounter>
      </ThreadSelectorContainer>
    );
  };

  const getRerunMenu = modalData => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={500}>
            Re-run Component
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid
            container
            sx={{
              backgroundColor: 'rgba(176, 0, 32, 0.05)',
              border: '1px solid rgba(176, 0, 32, 0.1)',
              borderRadius: '4px',
              padding: '12px',
            }}
          >
            <Grid item xs={12} sx={{ marginBottom: '8px' }}>
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 500,
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              >
                <Error sx={{ color: '#B00020', marginRight: '8px', fontSize: '20px' }} />
                This action will impact all downstream components
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                Run-running this component will impact the entire workflow and all downstream
                components. All data from downstream components that have already been run will be
                saved and then archived as a historical run. A new run of this workflow session will
                then be started. Once you have made your changes, click confirm edits and re-run to
                re-run the session from this point.
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ marginTop: '8px' }}>
          <ActionButton
            hoverBgColor="#1976D2"
            onClick={() => {
              setShowRerunMenu(false);
              rerunComponentSession(sessionId, modalData);
            }}
            disabled={terminateLoading || !isLatestRun}
            startIcon={<ReplayRounded />}
          >
            Re-run
            {terminateLoading && <Loader size={16} />}
          </ActionButton>
        </Grid>
      </Grid>
    );
  };

  const getReRunLoader = () => {
    return (
      <Grid container padding="16px" spacing={2}>
        <Grid item xs={12}>
          <StyledSkeleton variant="rectangular" height="30px" width="100%" />
        </Grid>
        <Grid item xs={12}>
          <StyledSkeleton variant="rectangular" height="60px" width="100%" />
        </Grid>
        <Grid item xs={12}>
          <StyledSkeleton variant="rectangular" height="200px" width="100%" />
        </Grid>
        <Grid item xs={12}>
          <StyledSkeleton variant="rectangular" height="200px" width="100%" />
        </Grid>
      </Grid>
    );
  };

  const downloadComponentData = component => {
    const componentData =
      component.data && component.data.data
        ? component.data.data
        : component.data
        ? component.data
        : {};
    const dataStr = JSON.stringify(componentData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `component_${component.componentId}_data.json`;

    let linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const getHeaderTitle = component => {
    if (!component) return 'Component Details';

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Assignment sx={{ fontSize: '16px', color: 'text.primary' }} />
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '18px',
            color: 'text.primary',
          }}
        >
          {getComponentDisplayName(component.type)}
        </Typography>
        {getDisplayStatus(component) && (
          <Box sx={{ ml: 0.5 }}>{renderStatus(getDisplayStatus(component), true)}</Box>
        )}
      </Box>
    );
  };

  const getHeaderSubtitle = component => {
    if (!component) return '';
    return format(component.startAt, useProjectStore.getState().timezone, 'timeline');
  };

  const fetchSessionModalContent = () => {
    // If load re-run, get the re-run loader
    if (loadReRun) return getReRunLoader();

    // Check if it has been ran
    if (modalData.hasRun) {
      if (!isShowRerunMenu) {
        return <>{getBase(modalData, classes)}</>;
      } else {
        return getRerunMenu(modalData);
      }
    } else {
      // Component has not been run
      return (
        <Grid
          container
          sx={{
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '24px',
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Info sx={{ fontSize: '48px', color: 'text.disabled', marginBottom: '16px', opacity: 0.7 }} />
          <Typography variant="h6" sx={{ fontWeight: 500, marginBottom: '8px' }}>
            This component has not been run yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Once the workflow reaches this component, you'll see its data and status here.
          </Typography>
        </Grid>
      );
    }
  };

  return (
    <Drawer
      open={isOpen}
      onClose={() => {
        setModalData({});
        setShowRerunMenu(false);
        setIsExpanded(false);
      }}
      disableAutoFocus
      anchor={'right'}
      hideBackdrop={true}
      variant={'persistent'}
      PaperProps={{
        sx: {
          width:
            isExpanded && !isSplitView
              ? 'calc(100vw - 47px)'
              : isSplitView
              ? config.SESSION_LOGS_COMPONENT_INFO_PANE_WIDTH_IN_SPLIT_VIEW
              : config.SESSION_LOGS_COMPONENT_INFO_PANE_WIDTH,
          position: isSplitView ? 'absolute' : 'fixed',
          borderTopRightRadius: isSplitView ? '8px' : '0px',
          borderBottomRightRadius: isSplitView ? '8px' : '0px',
          top: isSplitView ? 0 : '57px',
          right: 0,
          height: isSplitView ? '100%' : 'calc(100% - 57px)',
          transition: 'width 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {isOpen && (
        <>
          <PanelHeader
            title={modalData.hasRun ? getHeaderTitle(modalData) : 'Component Details'}
            subtitle={modalData.hasRun ? getHeaderSubtitle(modalData) : ''}
            showExpandButton={true}
            showCloseButton={true}
            isExpanded={isExpanded}
            onExpand={() => setIsExpanded(!isExpanded)}
            onClose={() => {
              setModalData({});
              setShowRerunMenu(false);
              setIsExpanded(false);
            }}
          />
          <Grid
            container
            sx={{
              height: '100%',
              padding: '20px',
              overflow: 'auto',
              backgroundColor: 'background.paper',
              flex: 1,
            }}
          >
            <Grid item xs={12}>
              {fetchSessionModalContent()}
            </Grid>
          </Grid>
        </>
      )}
    </Drawer>
  );
}

SessionInfoModal.propTypes = {
  modalData: PropTypes.object.isRequired,
  setModalData: PropTypes.func.isRequired,
  classes: PropTypes.element.isRequired,
  sessionId: PropTypes.string.isRequired,
  getWorkflowProgressData: PropTypes.func.isRequired,
  triggerRefresh: PropTypes.func,
};

export default SessionInfoModal;
